// /frontend/src/App.js - Updated with PackageProvider & no Bootstrap
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider, useSelector, useDispatch } from 'react-redux';
import { verifyToken } from './store/slices/authSlice';
import store from './store';
import setAuthToken from './utils/setAuthToken';
import { CheckoutProvider } from './contexts/CheckoutContext';
import { PackageProvider } from './contexts/PackageContext';

// Pages
import HomePage from './pages/HomePage';
import CustomDesign from './pages/CustomDesign';
import CustomDesignConfirmation from './pages/CustomDesignConfirmation';
import CustomDesignDetail from './pages/CustomDesignDetail';
import DesignerPackages from './pages/DesignerPackages';
import MarketplacePage from './pages/marketplace/MarketplacePage';
import DesignersPage from './pages/marketplace/DesignersPage';
import PackagesPage from './pages/marketplace/PackagesPage';
import ProductDetailPage from './pages/product/ProductDetailPage';
import CartPage from './pages/cart/CartPage';
import WishlistPage from './pages/wishlist/WishlistPage';
import ProfilePage from './pages/profile/ProfilePage'; 
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import NotFoundPage from './pages/NotFoundPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';

// Checkout Pages
import ShippingPage from './pages/checkout/ShippingPage';
import PaymentPage from './pages/checkout/PaymentPage';
import PlaceOrderPage from './pages/checkout/PlaceOrderPage';
import OrderConfirmationPage from './pages/checkout/OrderConfirmationPage';
import DesignCheckoutPage from './pages/checkout/DesignCheckoutPage';

// Account Pages
import MyOrdersPage from './pages/account/MyOrdersPage';
import OrderDetailPage from './pages/account/OrderDetailPage';
import DesignRequestsPage from './pages/account/DesignRequestsPage';

// Admin Pages
import AdminLoginPage from './pages/admin/AdminLoginPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminProducts from './pages/admin/AdminProducts';
import AdminUsers from './pages/admin/AdminUsers';
import AdminOrders from './pages/admin/AdminOrders';
import AdminOrderDetail from './pages/admin/AdminOrderDetail';
import AdminSalesReports from './pages/admin/AdminSalesReports';
import AdminCategories from './pages/admin/AdminCategories';
import AdminInventory from './pages/admin/AdminInventory';
import AdminSettings from './pages/admin/AdminSettings';
import BulkProductImport from './pages/admin/BulkProductImport';
import AdminDesignRequests from './pages/admin/AdminDesignRequests';
import AdminDesignDetail from './pages/admin/AdminDesignDetail';

// Admin Form Components
import ProductForm from './pages/admin/ProductForm';
import UserForm from './pages/admin/UserForm';

// Route Guards
import PrivateRoute from './components/routes/PrivateRoute';
import SellerRoute from './components/routes/SellerRoute';
import AdminRoute from './components/routes/AdminRoute';

import OurStoryPage from './pages/OurStoryPage';
import WarrantyPage from './pages/WarrantyPage';
import ReturnsPage from './pages/ReturnsPage';
import ShippingPageFooter from './pages/ShippingPage';
import TermsPage from './pages/TermsPage';

// Set auth token for all requests if token exists
if (localStorage.token) {
  setAuthToken(localStorage.token);
}

// AppContent component to use hooks inside Routes
const AppContent = () => {
  const dispatch = useDispatch();
  
  // Verify token and load user on first mount
  useEffect(() => {
    dispatch(verifyToken());
  }, [dispatch]);
  
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Navigate to="/designer-packages" />} />
      <Route path="/browse" element={<Navigate to="/category/all" />} />
      
      {/* Main option routes */}
      <Route path="/custom-design" element={<CustomDesign />} />
      <Route path="/custom-design/confirmation/:requestId" element={
        <PrivateRoute>
          <CustomDesignConfirmation />
        </PrivateRoute>
      } />
      <Route path="/custom-design/request/:requestId" element={
        <PrivateRoute>
          <CustomDesignDetail />
        </PrivateRoute>
      } />
      <Route path="/designer-packages" element={<DesignerPackages />} />
      <Route path="/category/:categoryId" element={<HomePage />} />
      <Route path="/marketplace" element={<MarketplacePage />} />
      <Route path="/designers" element={<DesignersPage />} />
      <Route path="/packages" element={<PackagesPage />} />
      <Route path="/product/:productId" element={<ProductDetailPage />} />
      
      {/* Auth routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />

      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      {/* Protected user routes */}
      <Route path="/cart" element={
        <PrivateRoute>
          <CartPage />
        </PrivateRoute>
      } />
      <Route path="/cart/:id" element={
        <PrivateRoute>
          <CartPage />
        </PrivateRoute>
      } />
      <Route path="/wishlist" element={
        <PrivateRoute>
          <WishlistPage />
        </PrivateRoute>
      } />
      
      {/* Profile Route */}
      <Route path="/profile" element={
        <PrivateRoute>
          <ProfilePage />
        </PrivateRoute>
      } />

      {/* Checkout Routes */}
      <Route path="/shipping" element={
        <PrivateRoute>
          <ShippingPage />
        </PrivateRoute>
      } />
      <Route path="/payment/:orderId" element={
        <PrivateRoute>
          <PaymentPage />
        </PrivateRoute>
      } />
      <Route path="/placeorder" element={
        <PrivateRoute>
          <PlaceOrderPage />
        </PrivateRoute>
      } />
      <Route path="/order/confirmation/:orderId" element={
        <PrivateRoute>
          <OrderConfirmationPage />
        </PrivateRoute>
      } />
      <Route path="/checkout/design-request/:requestId" element={
        <PrivateRoute>
          <DesignCheckoutPage />
        </PrivateRoute>
      } />
      
      {/* Account Routes */}
      <Route path="/account/orders" element={
        <PrivateRoute>
          <MyOrdersPage />
        </PrivateRoute>
      } />
      <Route path="/account/orders/:orderId" element={
        <PrivateRoute>
          <OrderDetailPage />
        </PrivateRoute>
      } />
      <Route path="/account/design-requests" element={
        <PrivateRoute>
          <DesignRequestsPage />
        </PrivateRoute>
      } />

      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/admin/dashboard" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      
      {/* Admin Product Management */}
      <Route path="/admin/products" element={<AdminRoute><AdminProducts /></AdminRoute>} />
      <Route path="/admin/products/:id/edit" element={<AdminRoute><ProductForm isEditing={true} /></AdminRoute>} />
      <Route path="/admin/products/new" element={<AdminRoute><ProductForm /></AdminRoute>} />
      
      {/* Admin Category Management */}
      <Route path="/admin/categories" element={<AdminRoute><AdminCategories /></AdminRoute>} />
      
      {/* Admin Inventory Management */}
      <Route path="/admin/inventory" element={<AdminRoute><AdminInventory /></AdminRoute>} />
      
      {/* Admin User Management */}
      <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
      <Route path="/admin/users/:id/edit" element={<AdminRoute><UserForm isEditing={true} /></AdminRoute>} />
      <Route path="/admin/users/new" element={<AdminRoute><UserForm /></AdminRoute>} />
      
      {/* Admin Order Management */}
      <Route path="/admin/orders" element={<AdminRoute><AdminOrders /></AdminRoute>} />
      <Route path="/admin/orders/:id" element={<AdminRoute><AdminOrderDetail /></AdminRoute>} />
      <Route path="/admin/sales" element={<AdminRoute><AdminSalesReports /></AdminRoute>} />
      <Route path="/admin/products/bulk-import" element={<AdminRoute><BulkProductImport /></AdminRoute>} />

      {/* Admin Design Request */}
      <Route path="/admin/design-requests" element={<AdminRoute><AdminDesignRequests /></AdminRoute>} />
      <Route path="/admin/design-requests/:id" element={<AdminRoute><AdminDesignDetail /></AdminRoute>} />

      {/* Admin Settings */}
      <Route path="/admin/settings" element={<AdminRoute><AdminSettings /></AdminRoute>} />
      
      {/* Redirect /admin to /admin/dashboard */}
      <Route path="/admin" element={<Navigate to="/admin/dashboard" />} />

      <Route path="/our-story" element={<OurStoryPage />} />
      <Route path="/warranty" element={<WarrantyPage />} />
      <Route path="/returns" element={<ReturnsPage />} />
      <Route path="/shippingterm" element={<ShippingPageFooter />} />
      <Route path="/terms" element={<TermsPage />} />
      
      {/* 404 Route - Always keep this last */}
      <Route path="*" element={<NotFoundPage />} />
      
    </Routes>
  );
};

const App = () => {
  return (
    <Provider store={store}>
      <CheckoutProvider>
        <PackageProvider>
          <Router>
            <AppContent />
          </Router>
        </PackageProvider>
      </CheckoutProvider>
    </Provider>
  );
};

export default App;